import { CreditCard, Building2, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import CheckoutDisclaimer from "./CheckoutDisclaimer";
import OfferComparisonDrawer from "./OfferComparisonDrawer";
import { useState } from "react";

interface PaymentOptionsProps {
  amount?: number;
}

const PaymentOptionsSection = ({ amount = 25000 }: PaymentOptionsProps) => {
  const [showComparison, setShowComparison] = useState(false);
  
  const getPaymentOptions = (amount: number) => [
    {
      icon: CreditCard,
      title: "BNPL (Tabby, Tamara)",
      description: amount <= 20000 
        ? `Most BNPL providers cover up to AED 10-20K. Split into 3–4 payments (AED ${Math.round(amount / 3)}-${Math.round(amount / 4)}/payment), often 0%.`
        : "Most BNPL providers cover up to AED 10-20K, but families often face bigger costs.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Building2,
      title: "Bank Installments", 
      description: amount >= 20000 && amount <= 100000
        ? `AED 20K–100K. 12 months (AED ${Math.round(amount / 12)}/mo) or 24 months (AED ${Math.round(amount / 24)}/mo).`
        : "AED 20K–100K+ (card limits), 6–24 months.",
      gradient: "from-green-500 to-teal-500"
    },
    {
      icon: TrendingUp,
      title: "Extended Finance",
      description: amount >= 50000
        ? `AED 50,000–200,000+. Custom terms (e.g., AED ${Math.round(amount / 36)}/mo over 36 months).`
        : "AED 50,000–200,000+. Custom terms.",
      gradient: "from-purple-500 to-pink-500"
    },
  ];

  const paymentOptions = getPaymentOptions(amount);

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-flexaura-gray/50 to-background">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Payment Options Made Simple
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Most BNPL providers cover up to AED 10–20K, but families often face bigger costs. Flexaura brings BNPL, banks, and finance partners together so you can spread payments on bills from AED 20K to AED 200K.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {paymentOptions.map((option, index) => (
            <div key={index} className="bg-card rounded-2xl overflow-hidden card-shadow hover:elevated-shadow smooth-transition">
              {/* Gradient Header */}
              <div className={`bg-gradient-to-r ${option.gradient} p-6 text-white`}>
                <div className="flex items-center justify-center w-12 h-12 bg-white/20 rounded-lg mb-4">
                  <option.icon className="w-6 h-6 text-white" />
                </div>
                
                <h3 className="text-xl font-heading font-semibold mb-3 text-white">
                  {option.title}
                </h3>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <p className="text-sm text-muted-foreground font-body">
                  {option.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground font-body mb-2">
            All options shown side-by-side; we route the best fit automatically.
          </p>
          <p className="text-xs text-muted-foreground font-body mb-4">
            Illustrative only — final terms depend on partners.
          </p>
          <Button 
            variant="outline" 
            onClick={() => setShowComparison(true)}
            className="hover:bg-flexaura-teal hover:text-white"
          >
            See Example Comparison
          </Button>
        </div>
        
        <CheckoutDisclaimer />
        
        {/* Offer Comparison Drawer */}
        <OfferComparisonDrawer 
          isOpen={showComparison}
          onClose={() => setShowComparison(false)}
          amount={amount}
        />
      </div>
    </section>
  );
};

export default PaymentOptionsSection;